import { GridColDef, GridRenderCellParams, GridValueFormatter } from "@mui/x-data-grid-premium"
import { LeadRow, LookupMap, StringOptionMap } from "./leadTableTypes"
import { kebabCase } from "lodash-es"
import { Box, Checkbox, Chip } from "@mui/material"
import { ReactNode } from "react"
import { getStatusStyle } from "../components/StatusFilterButton"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatCurrencyFromCents: GridValueFormatter<LeadRow, any, any, never> = (value) => {
  if (value == null) {
    return ''
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value) / 100)
}

export function formatDateTime(value: unknown, timeZone: string) {
  if (!value) {
    return ''
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(String(value)))
}

export function renderDateTime(timeZone: string) {
  return function DateTimeCell(params: GridRenderCellParams<LeadRow, string | null>) {
    return (
      <Box sx={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(params.value, timeZone)}
      </Box>
    )
  }
}

export function renderBooleanChip(params: GridRenderCellParams<LeadRow, boolean>) {
  const value = Boolean(params.value)

  return (
    <Chip
      label={value ? 'Yes' : 'No'}
      color={value ? 'success' : 'error'}
      size='small'
      sx={{
        fontSize: '12px',
      }}
    />
  )
}

export function renderStatusChip(status: string | null | undefined) {
  return status ? (
    <Chip
      label={status}
      size='small'
      sx={{
        backgroundColor: getStatusStyle(kebabCase(status)),
        fontSize: '12px',
      }}
    />
  ) : null
}

export function renderHeaderWithFilter({
  title,
  filterNode,
}: {
  title?: string
  filterNode: ReactNode
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {filterNode}
      </Box>
      {title &&
        <Box
          sx={{
            flex: 1,
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            top: '1px',
          }}
        >
          {title}
        </Box>
      }
    </Box>
  )
}

export function renderBooleanHeaderCheckboxFilter(params: {
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        overflow: 'hidden',
        height: 32,
        width: 32,
      }}
    >
      <Checkbox
        disableRipple
        checked={params.checked}
        onChange={(event) => params.onChange(event.target.checked)}
      />
    </Box>
  )
}

export function formatLookupValue(value: unknown, lookup: LookupMap) {
  if (value == null || value === '') {
    return ''
  }

  return lookup[String(value)] ?? String(value)
}

export function buildLookupDisplayColumn<TField extends keyof LeadRow>(params: {
  field: TField
  headerName: string
  lookup: LookupMap
  minWidth?: number
  type?: GridColDef<LeadRow>['type']
  filterable?: boolean
  flex?: number
}) {
  const { field, headerName, lookup, minWidth = 140, type, filterable = true, flex = undefined } = params

  return {
    field,
    headerName,
    minWidth,
    type,
    filterable,
    valueFormatter: (value) => formatLookupValue(value, lookup),
    renderCell: (cellParams: GridRenderCellParams<LeadRow, LeadRow[TField]>) =>
      formatLookupValue(cellParams.row[field], lookup),
    align: 'left',
    headerAlign: 'left',
    flex: flex,
  } satisfies GridColDef<LeadRow>
}

export function buildOptionDisplayColumn<TField extends keyof LeadRow>(params: {
  field: TField
  headerName: string
  lookup: StringOptionMap
  minWidth?: number
  filterable?: boolean
  renderCell?: (params: GridRenderCellParams<LeadRow, LeadRow[TField]>) => ReactNode
}) {
  const { field, headerName, lookup, minWidth = 130, filterable = true, renderCell } = params


  return {
    field,
    headerName,
    minWidth,
    filterable,
    flex: 1,
    renderCell:
      renderCell ??
      ((cellParams: GridRenderCellParams<LeadRow, LeadRow[TField]>) =>
        formatLookupValue(cellParams.row[field], lookup)),
  } satisfies GridColDef<LeadRow>
}
